import { apiClient, ensureCsrfCookie } from '@/api/client'
import type { ApiSuccessResponse } from '@/types/api'
import type { AuthUser } from '@/types/auth'

export interface RegisterStudentPayload {
  name: string
  email: string
  phone: string
  password: string
  password_confirmation: string
}

export interface RegisterRecruiterPayload {
  organization_name: string
  website?: string
  industry?: string
  name: string
  email: string
  phone: string
  password: string
  password_confirmation: string
}

export interface LoginPayload {
  email: string
  password: string
}

export interface LoginResult {
  requiresTwoFactor: boolean
  twoFactorToken?: string
  mustEnrollTwoFactor?: boolean
  user?: AuthUser
}

export interface TwoFactorVerifyPayload {
  twoFactorToken: string
  code: string
}

export interface TwoFactorSetupResult {
  secret: string
  provisioningUri: string
  qrCodeSvg: string
}

/**
 * Every call here first ensures a CSRF cookie exists — cheap (Laravel just
 * re-issues the same token if one is already valid) and avoids a class of
 * "works after refresh, fails on first load" bugs from a missing cookie.
 */
async function withCsrf<T>(fn: () => Promise<T>): Promise<T> {
  await ensureCsrfCookie()
  return fn()
}

export const authApi = {
  registerStudent: (payload: RegisterStudentPayload) =>
    withCsrf(async () => {
      const { data } = await apiClient.post<ApiSuccessResponse<AuthUser>>(
        '/auth/register/student',
        payload,
      )
      return data.data
    }),

  registerRecruiter: (payload: RegisterRecruiterPayload) =>
    withCsrf(async () => {
      const { data } = await apiClient.post<ApiSuccessResponse<AuthUser>>(
        '/auth/register/recruiter',
        payload,
      )
      return data.data
    }),

  login: (payload: LoginPayload) =>
    withCsrf(async () => {
      const { data } = await apiClient.post<ApiSuccessResponse<LoginResult>>('/auth/login', payload)
      return data.data
    }),

  logout: async () => {
    await apiClient.post('/auth/logout')
  },

  me: async () => {
    const { data } = await apiClient.get<ApiSuccessResponse<AuthUser>>('/auth/me')
    return data.data
  },

  verifyEmail: (payload: { email: string; code: string }) =>
    withCsrf(async () => {
      const { data } = await apiClient.post<ApiSuccessResponse<AuthUser>>(
        '/auth/verify-email',
        payload,
      )
      return data.data
    }),

  resendVerification: (payload: { email: string }) =>
    withCsrf(() => apiClient.post('/auth/resend-verification', payload)),

  forgotPassword: (payload: { email: string }) =>
    withCsrf(() => apiClient.post('/auth/forgot-password', payload)),

  resetPassword: (payload: {
    email: string
    code: string
    password: string
    password_confirmation: string
  }) => withCsrf(() => apiClient.post('/auth/reset-password', payload)),

  setupAccount: (payload: {
    email: string
    code: string
    password: string
    password_confirmation: string
  }) => withCsrf(() => apiClient.post('/auth/setup-account', payload)),

  confirmPassword: (payload: { password: string }) =>
    apiClient.post('/auth/confirm-password', payload),

  twoFactorSetup: async () => {
    const { data } =
      await apiClient.post<ApiSuccessResponse<TwoFactorSetupResult>>('/auth/two-factor/setup')
    return data.data
  },

  twoFactorConfirm: async (payload: { code: string }) => {
    const { data } = await apiClient.post<ApiSuccessResponse<{ recoveryCodes: string[] }>>(
      '/auth/two-factor/confirm',
      payload,
    )
    return data.data
  },

  twoFactorVerifyLogin: (payload: TwoFactorVerifyPayload) =>
    withCsrf(async () => {
      const { data } = await apiClient.post<ApiSuccessResponse<{ user: AuthUser }>>(
        '/auth/two-factor/verify',
        payload,
      )
      return data.data
    }),
}
